import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LandingCarouselConfig } from '../lib/types';
import { Button, cn } from './ui';

/**
 * Landing-page hero carousel. Super-admin configures the slides + interval from
 * /admin/settings; this just renders + auto-advances. Design goals:
 *
 *   - Crossfade between slides (opacity, GPU-cheap) rather than a horizontal slide,
 *     so mixed aspect-ratio images don't cause layout jank.
 *   - Auto-advance on the configured interval, PAUSED on hover + when the tab is
 *     hidden (no point cycling to an unwatched tab + burning paints).
 *   - Respects prefers-reduced-motion: no auto-advance, no fade animation; the dots
 *     still let a user step through manually.
 *   - Each slide can carry an optional headline / subtext / CTA overlaid on the image
 *     with a legibility scrim; image-only slides render clean.
 */
export function HeroCarousel({ config }: { config: LandingCarouselConfig }) {
  const slides = config.slides;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );

  const go = useCallback(
    (next: number) => setIndex(((next % slides.length) + slides.length) % slides.length),
    [slides.length],
  );

  // Auto-advance. Skipped when paused, when only one slide, or under reduced-motion.
  useEffect(() => {
    if (paused || slides.length <= 1 || reducedMotion.current) return;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, config.intervalMs);
    return () => window.clearInterval(id);
  }, [paused, slides.length, config.intervalMs]);

  // Pause cycling while the tab is hidden — restored on return.
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  if (slides.length === 0) return null;

  return (
    <div
      className="relative mt-8 overflow-hidden rounded-3xl border border-border bg-panel2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
    >
      {/* Fixed-aspect stage so slide swaps never reflow the page. */}
      <div className="relative aspect-[21/9] w-full">
        {slides.map((s, i) => (
          <div
            key={s.id}
            className={cn(
              'absolute inset-0 transition-opacity duration-700',
              i === index ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            aria-hidden={i !== index}
          >
            <img
              src={s.imageUrl}
              alt={s.headline ?? 'SieveGuard'}
              className={cn('h-full w-full object-cover', i === index && 'sg-carousel-slide')}
              loading={i === 0 ? 'eager' : 'lazy'}
            />
            {(s.headline || s.subtext || s.ctaText) && (
              <>
                {/* Legibility scrim — gradient from the bottom-left where the copy sits. */}
                <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12">
                  {s.headline && (
                    <h2 className="max-w-2xl font-display text-2xl font-bold leading-tight text-white drop-shadow md:text-4xl">
                      {s.headline}
                    </h2>
                  )}
                  {s.subtext && (
                    <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-white/85 md:text-[15px]">
                      {s.subtext}
                    </p>
                  )}
                  {s.ctaText && s.ctaUrl && (
                    <div className="mt-5">
                      <CarouselCta text={s.ctaText} url={s.ctaUrl} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Dots — manual navigation. Hidden for a single slide. */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => go(i)}
              className={cn(
                'h-2 rounded-full transition-all',
                i === index ? 'w-6 bg-accent' : 'w-2 bg-white/40 hover:bg-white/70',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * CTA that handles both internal (react-router) and external (full URL) targets —
 * super-admins might point a slide at /pricing OR at an external blog post.
 */
function CarouselCta({ text, url }: { text: string; url: string }) {
  const external = /^https?:\/\//i.test(url);
  if (external) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <Button variant="primary" className="h-11 px-5">
          {text} →
        </Button>
      </a>
    );
  }
  return (
    <Link to={url}>
      <Button variant="primary" className="h-11 px-5">
        {text} →
      </Button>
    </Link>
  );
}
